import * as React from "react";
import { RootStore } from "../../../stores/root-store";
import { Icon, Input, Table } from "semantic-ui-react";
import SVG from "react-inlinesvg";
import updatedIcon from "../../../assets/images/icons/selected/arrow_circle2.svg";
import { useLoaderData } from "react-router-dom";
import { useItemFilter } from "../../../hooks/use-item-filter";
import { useEffect } from "react";
import { CreationArea } from "../../../stores/area/area";
import { Constants } from "../../../globals/constants";
import { getLabel } from "../../../reflection/reflection";
import { CreationProject } from "../../../stores/project/project";
import { AppState } from "../../../stores/app-mobx";
import { observer } from "mobx-react";

const searchTextKey = "report-creation-areas";
const sortAttributeKey = "report-creation-areas";

type LoadResult = {
    creationAreas: CreationArea[];
    appStore: AppState;
};

export async function loader(rootStore: RootStore, appStore: AppState) {
    const rootStoreSession = rootStore.newSession();
    const creationAreas = await rootStoreSession.areaStore.loadAllCreationAreas();
    await rootStoreSession.activityLogEntryStore.load({ tableName: Constants.creationAreaExtTableName });

    return { creationAreas, appStore };
}

const ReportCreationAreas = observer(() => {
    const { creationAreas, appStore } = useLoaderData() as LoadResult;
    const searchText = appStore.searchTexts.get(searchTextKey) ?? "";
    const sortConfiguration = appStore.sortAttributes.get(sortAttributeKey) ?? {
        attribute: "id",
        direction: "ascending"
    };
    const [state, dispatch] = useItemFilter<CreationArea>(sortConfiguration.attribute, sortConfiguration.direction, creationAreas, []);
    const { sortAttribute, sortDirection, items } = state;

    useEffect(() => {
        appStore.setSortConfiguration(sortAttributeKey, { attribute: sortAttribute, direction: sortDirection });
    }, [sortAttribute, sortDirection]);

    return (
        <div>
            <Input
                id="search-field"
                placeholder="Suche nach ID"
                defaultValue={searchText}
                type="search"
                icon={
                    <Icon
                        name="delete"
                        link
                        onClick={() => {
                            const input = document.getElementById(
                                "search-field"
                            ) as HTMLInputElement;
                            input.value = "";
                            appStore.setSearchText(searchTextKey, "");
                        }}
                    />
                }
                onChange={(event) => appStore.setSearchText(searchTextKey, event.target.value)}
            />
            <Table celled={true} collapsing={true} compact="very" selectable={true} singleLine={true} size="small"
                   sortable={true}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell sorted={sortAttribute === "id" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "id" })}
                                          textAlign="center">
                            {getLabel(CreationArea.prototype, "id")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "projectId" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "projectId" })}
                                          textAlign="center">
                            {getLabel(CreationArea.prototype, "projectId")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "stage" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "stage" })}
                                          textAlign="center">
                            {getLabel(CreationArea.prototype, "stage")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "png" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "png" })}
                                          textAlign="right">
                            {getLabel(CreationArea.prototype, "pngDisplayText")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "nek" ? sortDirection : null}
                                          onClick={() => dispatch({ type: "changeSort", sortAttribute: "nek" })}
                                          textAlign="center">
                            {getLabel(CreationArea.prototype, "nek")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "eligibilityt" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "eligibility"
                                          })}
                                          textAlign="right">
                            {getLabel(CreationArea.prototype, "eligibilityDisplayText")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "effectiveAreaRounded" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "effectiveAreaRounded"
                                          })}
                                          textAlign="right">
                            {getLabel(CreationArea.prototype, "effectiveAreaRounded")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "creationStatus" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "creationStatus"
                                          })}>
                            {getLabel(CreationArea.prototype, "creationStatus")}
                        </Table.HeaderCell>
                        <Table.HeaderCell sorted={sortAttribute === "updatedAtTimestamp" ? sortDirection : null}
                                          onClick={() => dispatch({
                                              type: "changeSort",
                                              sortAttribute: "updatedAtTimestamp"
                                          })}
                                          textAlign="center">
                            <SVG src={updatedIcon} height="auto" width={20}
                                 title={getLabel(CreationProject.prototype, "updatedAt")}/>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.filter(p => p.id.toString().toLowerCase().includes(searchText.toLowerCase())).map((p) => (
                        <Table.Row key={p.id}>
                            <Table.Cell textAlign="center">{p.id}</Table.Cell>
                            <Table.Cell textAlign="center">{p.projectId}</Table.Cell>
                            <Table.Cell textAlign="center">{p.stage}</Table.Cell>
                            <Table.Cell textAlign="right">{p.png?.toLocaleString()}</Table.Cell>
                            <Table.Cell textAlign="center">{p.nek}</Table.Cell>
                            <Table.Cell textAlign="right">{p.eligibilityDisplayText}</Table.Cell>
                            <Table.Cell textAlign="right">{p.effectiveAreaRounded.toLocaleString()}</Table.Cell>
                            <Table.Cell>{p.creationStatus}</Table.Cell>
                            <Table.Cell textAlign="center">{p.updatedAt}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
})

export default ReportCreationAreas;
